import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import { startHubSpotOAuth, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { FormField } from "./FormField";
import { hubSpotFormSchema, type HubSpotForm } from "./integrationFormSchemas";

export function HubSpotConnectionCard({ connection, onSaved, onCancel }: { connection?: IntegrationConnection; onSaved: () => Promise<void>; onCancel: () => void }) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const { register, handleSubmit, getValues, formState: { errors } } = useForm<HubSpotForm>({
    resolver: zodResolver(hubSpotFormSchema),
    defaultValues: { name: connection?.name ?? "HubSpot" },
  });

  const authorize = async (values = getValues()) => {
    const { authorizationUrl } = await startHubSpotOAuth({ name: values.name, ...(connection ? { connectionId: connection.id } : {}) });
    action.redirect(authorizationUrl);
  };

  const save = handleSubmit((values) => action.run(connection ? "save" : "authorize", async () => {
    try {
      if (!connection) return await authorize(values);
      await updateIntegrationConnection(connection.id, { name: values.name });
      toast.success("HubSpot settings updated.");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to connect HubSpot.");
    }
  }));

  const reconnect = handleSubmit((values) => action.run("authorize", async () => {
    try { await authorize(values); }
    catch (error) { toast.error(error instanceof Error ? error.message : "Unable to authorize connection."); }
  }));

  return (
    <form className="space-y-4" onSubmit={save} noValidate>
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        HubSpot is read-only in this first slice. IO Spoke requests access to deals, then imports bounded CRM context without changing your records.
      </div>
      <FormField id="hubspot-name" label="Connection name" registration={register("name")} error={errors.name} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={isSubmitting} loading={connection ? action.saving : action.authorizing} loadingLabel={connection ? "Saving…" : "Authorizing…"}>
          {connection ? "Save settings" : <> <ExternalLink className="mr-2 h-4 w-4" />Continue to HubSpot</>}
        </Button>
        {connection ? (
          <Button type="button" variant="outline" onClick={reconnect} disabled={isSubmitting} loading={action.authorizing} loadingLabel="Authorizing…">
            Reconnect HubSpot
          </Button>
        ) : null}
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
